import { supabase } from './supabase';

export const deliveryStages = [
  'draft',
  'booked',
  'packed',
  'dispatched',
  'in_transit',
  'out_for_delivery',
  'delivered',
  'cancelled',
] as const;
export type DeliveryStage = (typeof deliveryStages)[number];
export type FulfillmentMethod = 'unspecified' | 'courier' | 'local_delivery' | 'pickup';
export const deliveryLabel = (value?: string | null) =>
  value === 'draft'
    ? 'Not ready'
    : (value || 'Needs method').replaceAll('_', ' ').replace(/^./, (c) => c.toUpperCase());
export const fulfillmentOptions = [
  { value: 'courier', label: 'Courier', sublabel: 'Shipment with courier tracking' },
  { value: 'local_delivery', label: 'Local delivery', sublabel: 'Delivered by your team' },
  { value: 'pickup', label: 'Customer pickup', sublabel: 'Managed in Sales' },
];
export interface DeliveryRow {
  allowed_transitions?: DeliveryStage[];
  sale_id: string;
  sale_number: string;
  sale_date: string;
  sale_updated_at: string;
  customer_name: string | null;
  customer_mobile: string | null;
  customer_address: string | null;
  sale_status: string;
  fulfillment_method: FulfillmentMethod;
  delivery_id: string | null;
  stage: DeliveryStage | null;
  version: number | null;
  updated_at: string;
  courier_name: string | null;
  tracking_number: string | null;
  website_url: string | null;
  tracking_url: string | null;
  packing_requires_review: boolean;
  issue: string | null;
  address_confirmation: string | null;
}
export interface DeliveryEvent {
  id: string;
  action: string;
  from_stage: string | null;
  to_stage: string;
  actor_name: string | null;
  reason: string | null;
  internal_note: string | null;
  recorded_at: string;
  occurred_at: string | null;
  delivery_version: number;
}
export interface DeliveryDetail extends DeliveryRow {
  events: DeliveryEvent[];
  items: {
    id: string;
    name: string;
    size: string | null;
    color: string | null;
    quantity: number;
  }[];
}
export interface DeliveryFilters {
  view?: string;
  search?: string;
  stage?: string;
  method?: string;
  carrier?: string;
  issue?: string;
  from?: string;
  to?: string;
  offset?: number;
  limit?: number;
}
export interface DeliveryOutcome {
  sale_id: string;
  outcome: 'updated' | 'unchanged' | 'skipped' | 'conflict' | 'failed';
  message?: string;
}
export interface DeliveryShare {
  sale_id: string;
  sale_number: string;
  customer_name: string | null;
  customer_mobile: string | null;
  sale_updated_at: string;
  version: number;
  stage: DeliveryStage;
  fulfillment_method: FulfillmentMethod;
  courier_name: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  website_url?: string | null;
  issue: string | null;
}
export function deliveryError(error: unknown): string {
  const e = error as { code?: string; message?: string };
  if (['PGRST202', 'PGRST205', '42P01', '42703'].includes(e?.code || ''))
    return 'Delivery setup is required. Ask your administrator to run the Delivery database update, then retry.';
  return e?.message || 'Delivery could not be loaded. Please retry.';
}
export async function deliveryRpc<T>(
  name: string,
  args: Record<string, unknown> = {},
  signal?: AbortSignal,
): Promise<T> {
  let request = supabase.rpc(name, args);
  if (signal) request = request.abortSignal(signal);
  const { data, error } = await request;
  if (error) throw new Error(deliveryError(error));
  return data as T;
}

/** The installed share RPC omits website_url; use the existing authorized detail read.
 * Compare both saved versions before combining this single, allowlisted field.
 */
export async function fetchDeliveryShare(
  saleId: string,
  signal?: AbortSignal,
): Promise<DeliveryShare> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const [share, detail] = await Promise.all([
      deliveryRpc<DeliveryShare>('get_delivery_whatsapp_payload', { p_sale_id: saleId }, signal),
      deliveryRpc<DeliveryRow>('get_sale_delivery', { p_sale_id: saleId }, signal),
    ]);
    if (share.sale_id !== saleId || detail.sale_id !== saleId)
      throw new Error('This delivery is no longer available. Refresh and try again.');
    if (
      share.version === detail.version &&
      Date.parse(share.sale_updated_at) === Date.parse(detail.sale_updated_at)
    )
      return { ...share, website_url: detail.website_url };
  }
  throw new Error(
    'The delivery changed while loading. Refresh and review the message before opening WhatsApp.',
  );
}
export const deliverySnapshot = (row: DeliveryRow) => ({
  sale_id: row.sale_id,
  expected_sale_updated_at: row.sale_updated_at,
  expected_delivery_version: row.version,
});
export function nextDeliveryStages(row: DeliveryRow, correction = false): DeliveryStage[] {
  if (['returned', 'partially_returned'].includes(row.sale_status)) return [];
  if (correction)
    return row.stage === 'delivered'
      ? ['dispatched']
      : ['dispatched', 'in_transit', 'out_for_delivery'].includes(row.stage || '')
        ? ['packed']
        : [];
  if (row.allowed_transitions) return row.allowed_transitions;
  switch (row.stage) {
    case 'draft':
      return ['cancelled'];
    case 'booked':
      return ['packed', 'cancelled'];
    case 'packed':
      return row.fulfillment_method === 'local_delivery'
        ? ['dispatched', 'delivered', 'booked', 'cancelled']
        : ['dispatched', 'booked', 'cancelled'];
    case 'dispatched':
      return ['in_transit', 'out_for_delivery', 'delivered'];
    case 'in_transit':
      return ['out_for_delivery', 'delivered'];
    case 'out_for_delivery':
      return ['delivered'];
    default:
      return [];
  }
}
export function publicTrackingUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const u = new URL(value);
    if (
      u.protocol !== 'https:' ||
      u.username ||
      u.password ||
      u.port ||
      !/^[a-z\d.-]+\.[a-z]{2,}$/i.test(u.hostname) ||
      /\.(local|internal|localhost|test|invalid|example)$/i.test(u.hostname)
    )
      return null;
    return u.href;
  } catch {
    return null;
  }
}
const clean = (value: string | null) => (value || '').replace(/[*_~`\r\n]/g, ' ').trim();
export function deliveryWhatsAppMessage(p: DeliveryShare): string {
  const stageMessages: Record<DeliveryStage, string> = {
    draft: '📝 *Order saved*\nYour order is awaiting confirmation.',
    booked: '☑️ *Order booked*\nYour order has been received.',
    packed: '📦 *Packed & ready*\nYour order is packed and ready for delivery.',
    dispatched: '🚚 *Dispatched*\nYour order has been dispatched.',
    in_transit: '🚚 *In transit*\nYour parcel is on its way.',
    out_for_delivery: '📍 *Out for delivery*\nYour order is out for delivery.',
    delivered: '✅ *Delivered*\nYour order has been delivered. We hope you love it!',
    cancelled: '❌ *Order cancelled*\nYour order has been cancelled.',
  };
  const lines = [
    '*KLOTHIQ* 🤍',
    '*Delivery update*',
    `Order *${clean(p.sale_number)}*`,
    '──────────────────',
    `Hi ${clean(p.customer_name) ? `*${clean(p.customer_name)}*` : 'there'},`,
    '',
    p.issue ? '⚠️ *Delivery needs attention*' : stageMessages[p.stage],
  ];
  if (p.issue)
    lines.push(
      p.issue === 'failed_attempt'
        ? 'The delivery attempt was unsuccessful. Please reply so we can help with the next step.'
        : p.issue === 'return_to_origin'
          ? 'Your parcel is being returned to us. Please reply for the next steps.'
          : p.issue === 'delay'
            ? 'Your delivery has been delayed. Please reply if you need help with the update.'
            : 'There is an update that needs your attention. Please reply so we can help.',
      `Last recorded stage: *${deliveryLabel(p.stage)}*`,
    );
  if (clean(p.courier_name) || clean(p.tracking_number)) lines.push('');
  if (clean(p.courier_name)) lines.push(`📦 Courier: *${clean(p.courier_name)}*`);
  if (clean(p.tracking_number)) lines.push(`Tracking no.: *${clean(p.tracking_number)}*`);
  const tracking = publicTrackingUrl(p.tracking_url);
  const website = publicTrackingUrl(p.website_url ?? null);
  if (tracking) lines.push('', '🔗 *Track your parcel*', tracking);
  if (website && website !== tracking) {
    lines.push('', '🌐 *Courier website*', website);
    if (!tracking && clean(p.tracking_number))
      lines.push('Use the tracking number above to check your parcel.');
  }
  lines.push(
    '──────────────────',
    'Need help? Reply to this message.',
    'Thank you for shopping with KLOTHIQ 🤍',
  );
  return lines.join('\n');
}
